// src/hooks/useApi.ts - Custom hooks with proper TypeScript

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import type {
  UseApiDataReturn,
  UseHomepageReturn,
  Homepage,
  NewsArticle,
  Organization,
  ResearchTheme,
  SiteSettings,
  ContactInformation,
  Person,
  NewsCategory,
  PersonType,
  ApiError,
  NetworkError,
} from '@/types';

// =====================
// GENERIC API HOOK
// =====================

export function useApiData<T>(
  fetcher: () => Promise<T>,
  dependencies: React.DependencyList = [],
  options?: {
    immediate?: boolean;
    onSuccess?: (data: T) => void;
    onError?: (error: string) => void;
  }
): UseApiDataReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(options?.immediate !== false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    try {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      setError(null);
      
      const result = await fetcher();
      
      // Check if request was cancelled
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }
      
      setData(result);
      options?.onSuccess?.(result);
    } catch (err) {
      // Don't update state if request was cancelled
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }
      
      let errorMessage = 'Unknown error occurred';
      
      if (err instanceof ApiError) {
        errorMessage = `API Error (${err.status}): ${err.message}`;
      } else if (err instanceof NetworkError) {
        errorMessage = `Network Error: ${err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      options?.onError?.(errorMessage);
      console.error('API fetch error:', err);
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, dependencies);

  useEffect(() => {
    if (options?.immediate !== false) {
      refetch();
    }
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { data, loading, error, refetch };
}

// =====================
// SPECIFIC API HOOKS
// =====================

// Homepage hook
export const useHomepage = (): UseHomepageReturn => {
  const { data, loading, error } = useApiData(
    () => api.getHomepage(),
    []
  );

  return {
    homepage: data,
    loading,
    error,
  };
};

// News articles hook
export const useNewsArticles = (params?: {
  page?: number;
  pageSize?: number;
  featured?: boolean;
  category?: NewsCategory;
  immediate?: boolean;
}) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getNewsArticles(params),
    [params?.page, params?.pageSize, params?.featured, params?.category],
    { immediate: params?.immediate }
  );

  return {
    articles: data || [],
    loading,
    error,
    refetch,
  };
};

// Latest news hook
export const useLatestNews = (limit: number = 5) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getLatestNews(limit),
    [limit]
  );

  return {
    articles: data || [],
    loading,
    error,
    refetch,
  };
};

// Featured news hook
export const useFeaturedNews = (limit: number = 3) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getFeaturedNews(limit),
    [limit]
  );

  return {
    articles: data || [],
    loading,
    error,
    refetch,
  };
};

// Organizations hook
export const useOrganizations = () => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getPartnerOrganizations(),
    []
  );

  return {
    organizations: data || [],
    loading,
    error,
    refetch,
  };
};

// Featured Partners hook
export const useFeaturedPartners = () => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getFeaturedPartners(),
    []
  );

  return {
    featuredPartners: data || { primary: [], secondary: [], tertiary: [] },
    primaryPartners: data?.primary || [],
    secondaryPartners: data?.secondary || [],
    tertiaryPartners: data?.tertiary || [],
    loading,
    error,
    refetch,
  };
};

// Research themes hook
export const useResearchThemes = (maxThemes?: number) => {
  const { data, loading, error, refetch } = useApiData(
    async () => {
      const themes = await api.getResearchThemes();
      return maxThemes ? themes.slice(0, maxThemes) : themes;
    },
    [maxThemes]
  );

  return {
    themes: data || [],
    loading,
    error,
    refetch,
  };
};

// Resources hook
export const useResources = (params?: {
  resourceType?: ResourceType;
  category?: string;
  accessLevel?: string;
  immediate?: boolean;
}) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getResources(params),
    [params?.resourceType, params?.category, params?.accessLevel],
    { immediate: params?.immediate }
  );

  return {
    resources: data || [],
    loading,
    error,
    refetch,
  };
};

// Public resources hook
export const usePublicResources = (limit?: number) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getPublicResources(limit),
    [limit]
  );

  return {
    resources: data || [],
    loading,
    error,
    refetch,
  };
};

// Resources by type hook
export const useResourcesByType = (resourceType: ResourceType, limit?: number) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getResourcesByType(resourceType, limit),
    [resourceType, limit]
  );

  return {
    resources: data || [],
    loading,
    error,
    refetch,
  };
};

// People hook
export const usePeople = (personType?: PersonType) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getPeople(personType),
    [personType]
  );

  return {
    people: data || [],
    loading,
    error,
    refetch,
  };
};

// Advisory Board hook
export const useAdvisoryBoard = () => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getAdvisoryBoardMembers(),
    []
  );

  return {
    advisoryMembers: data || [],
    loading,
    error,
    refetch,
  };
};

// Site settings hook
export const useSiteSettings = () => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getSiteSettings(),
    []
  );

  return {
    siteSettings: data,
    loading,
    error,
    refetch,
  };
};

// Contact information hook
export const useContactInformation = () => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getContactInformation(),
    []
  );

  return {
    contactInfo: data,
    loading,
    error,
    refetch,
  };
};

// =====================
// COMPOSITE HOOKS
// =====================

// Hook for homepage data (combines multiple API calls)
export const useHomepageData = () => {
  const { homepage, loading: homepageLoading, error: homepageError } = useHomepage();
  const { articles: featuredNews, loading: newsLoading, error: newsError } = useFeaturedNews(3);
  const { organizations: partners, loading: partnersLoading, error: partnersError } = useOrganizations();

  return {
    homepage,
    featuredNews,
    partners,
    loading: homepageLoading || newsLoading || partnersLoading,
    error: homepageError || newsError || partnersError,
  };
};

// Hook for navigation data
export const useNavigationData = () => {
  const { siteSettings, loading: settingsLoading, error: settingsError } = useSiteSettings();
  const { contactInfo, loading: contactLoading, error: contactError } = useContactInformation();

  return {
    siteSettings,
    contactInfo,
    loading: settingsLoading || contactLoading,
    error: settingsError || contactError,
  };
};

// =====================
// UTILITY HOOKS
// =====================

// Hook for debounced search
export const useDebouncedSearch = <T>(
  searchFunction: (query: string) => Promise<T[]>,
  delay: number = 300
) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const search = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (!searchQuery.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      timeoutRef.current = setTimeout(async () => {
        try {
          const data = await searchFunction(searchQuery);
          setResults(data);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Search failed');
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, delay);
    },
    [searchFunction, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    query,
    results,
    loading,
    error,
    search,
  };
};

// Hook for local storage with TypeScript
export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue];
};

// Events hook
export const useEvents = (params?: {
  upcoming?: boolean;
  eventType?: EventType;
  immediate?: boolean;
}) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getEvents(params),
    [params?.upcoming, params?.eventType],
    { immediate: params?.immediate }
  );

  return {
    events: data || [],
    loading,
    error,
    refetch,
  };
};

// Upcoming events hook
export const useUpcomingEvents = (limit?: number) => {
  const { data, loading, error, refetch } = useApiData(
    () => api.getUpcomingEvents(limit),
    [limit]
  );

  return {
    events: data || [],
    loading,
    error,
    refetch,
  };
};